'use client';
import React, { useState, useCallback, useMemo } from 'react';
import { Handshake, ArrowRight, Zap, Users, Target } from 'lucide-react';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';
import PartnershipFormModal from './modals/PartnershipFormModal';

const benefits = [
  {
    icon: Zap,
    title: 'Put Your Brand Where It Matters',
    description: 'Join our events, support workshops, or take part in key community moments.\n\nWe help your brand stand out in the right context and gain traction across different locations.',
  },
  {
    icon: Users,
    title: 'Tap into Early-Stage Movements',
    description: "ZeroClub is more than events. It's a growing network of emerging founders, creators, and teams.\n\nBy partnering with us, you get early access to ideas, momentum, and connections that shape what's next.",
  },
  {
    icon: Target,
    title: 'Be Seen Across European Cities',
    description: "We operate across cities and countries — not just one market. Your brand gets presence in new local communities and visibility that scales with our movement.\n\nPartner with ZeroClub and grow with a community that's expanding across Europe.",
  },
];

const BenefitCard = React.memo(({ item, idx }: { item: typeof benefits[0]; idx: number }) => {
  const Icon = item.icon;
  return (
    <div
      key={idx}
      className="group relative rounded-2xl p-[2px] bg-[linear-gradient(400deg,#0d273d_0%,#1a3b5c_55%,#d65c30_110%)] hover:bg-[#d65c30] shadow-xl shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
    >
      <div className="rounded-2xl bg-black/[0.03] backdrop-blur-xl border border-white/10 p-7 relative overflow-hidden h-full">
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
          <div className="absolute inset-0 bg-[#d65c30] bg-opacity-50" />
        </div>

        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f97316]/10 via-[#ec4899]/10 to-[#6366f1]/10 border border-white/10 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-[#f97316] drop-shadow-[0_0_8px_rgba(249,115,22,0.4)]" />
          </div>

          <h3 className="font-montserrat font-bold text-lg text-white mb-2">
            {item.title}
          </h3>

          <p className="font-montserrat text-slate-300 text-sm leading-relaxed whitespace-pre-line">
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );
});

BenefitCard.displayName = 'BenefitCard';

export default function PartnersInvitationSection() {
  const [openPartner, setOpenPartner] = useState(false);

  const handleOpenModal = useCallback(() => setOpenPartner(true), []);
  const handleCloseModal = useCallback(() => setOpenPartner(false), []);

  const benefitCards = useMemo(
    () => benefits.map((item, idx) => <BenefitCard key={idx} item={item} idx={idx} />),
    []
  );

  return (
    <>
      <SectionShell id="partners" bgClass="bg-navy-bottom">
        <div className="container mx-auto px-4">

          {/* HEADER */}
          <SectionHeader
            eyebrowText="Partner with Us"
            eyebrowIcon={Handshake}
            title="Partner with ZeroClub"
            subtitle="Build visibility. Stay relevant. Connect with a forward-thinking audience across Europe."
            titleGradientClassName="bg-gradient-to-r from-[#ff9a9e] via-[#f97316] to-[#ec4899]"
            textColorClass="text-white"
            align="center"
          />

          {/* BENEFITS */}
          <div className="relative z-10 max-w-6xl mx-auto mb-20">
            <div className="grid md:grid-cols-3 gap-7">
              {benefitCards}
            </div>
          </div>

          {/* CTA CARD */}
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="group relative p-[2px] rounded-3xl bg-[linear-gradient(400deg,#0d273d_0%,#1a3b5c_75%,#d65c30_115%)] shadow-xl shadow-black/40">
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-3xl bg-[#d65c3066] blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-700" />

              <div className="relative rounded-3xl backdrop-blur-2xl border border-white/10 p-10 text-center shadow-[0_0_35px_0px_#0d273d88]">
                <p className="font-montserrat text-[#c9d8e6] text-sm mb-7 max-w-md mx-auto">
                  Join leading companies supporting Europe's entrepreneurial community
                </p>

                <button
                  onClick={handleOpenModal}
                  className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-montserrat font-semibold text-white text-lg bg-[#d65c30] shadow-lg shadow-[#d65c30]/40 hover:shadow-[0_0_35px_6px_#d65c30aa] transition-all duration-300 hover:scale-[1.05] active:scale-95"
                >
                  Become a partner
                  <ArrowRight className="w-5 h-5" />
                </button>

                <p className="font-montserrat text-[#9fb3c9] text-xs mt-6">
                  Limited partnership slots available • Contact us for details
                </p>
              </div>
            </div>
          </div>
        </div>
      </SectionShell>
      <PartnershipFormModal
        open={openPartner}
        onClose={handleCloseModal}
      />
    </>
  );
}